/**
 * FeedbackModal Component
 *
 * Tabbed modal for submitting bug reports and feature requests.
 * Features:
 * - Segmented tabs (Bug | Feature request) - not wizard steps
 * - Bug tab: form with screenshot capture
 * - Feature request tab: quick submit + link to Feature Requests board
 * - Full light/dark theme support
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router'
import {
  X,
  Bug,
  Lightbulb,
  Camera,
  Upload,
  Loader2,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'
import { api } from '../../lib/api'
import { useTheme } from '../../contexts/ThemeContext'

import type { FeedbackContext, FeatureArea } from '../../types/feedback'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
}

type TabType = 'bug' | 'idea'
type ScreenshotState =
  | 'idle'
  | 'capturing'
  | 'ready'
  | 'too-large'
  | 'failed'
  | 'uploading'
  | 'upload-failed'

const MAX_SCREENSHOT_BYTES = 2 * 1024 * 1024
const SCREENSHOT_UPLOAD_BUDGET_BYTES = Math.floor(MAX_SCREENSHOT_BYTES * 0.95)
const JPEG_QUALITY_ATTEMPTS = [0.92, 0.86, 0.78, 0.7]
const TRANSPARENT_IMAGE_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='

const AREAS: FeatureArea[] = [
  'Portfolio',
  'Screener',
  'Discovery',
  'Strategies',
  'Integrations',
  'Other',
]

function restoreHiddenElements(elementsToHide: { el: HTMLElement; originalDisplay: string }[]) {
  elementsToHide.forEach(({ el, originalDisplay }) => {
    el.style.display = originalDisplay
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Screenshot encoding returned an empty image.'))
        }
      },
      'image/jpeg',
      quality
    )
  })
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Screenshot preview could not be created.'))
    reader.readAsDataURL(blob)
  })
}

function isTooLargeError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('too large')
}

async function encodeCanvasUnderUploadLimit(source: HTMLCanvasElement): Promise<Blob> {
  let smallestBlob: Blob | null = null

  for (const quality of JPEG_QUALITY_ATTEMPTS) {
    const blob = await canvasToBlob(source, quality)
    if (!smallestBlob || blob.size < smallestBlob.size) {
      smallestBlob = blob
    }
    if (blob.size <= SCREENSHOT_UPLOAD_BUDGET_BYTES) {
      return blob
    }
  }

  throw new Error(`Screenshot is still too large as JPEG: ${smallestBlob?.size ?? 0} bytes`)
}

async function encodeScreenshotCanvas(
  canvas: HTMLCanvasElement
): Promise<{ blob: Blob; dataUrl: string }> {
  const blob = await encodeCanvasUnderUploadLimit(canvas)
  return {
    blob,
    dataUrl: await blobToDataUrl(blob),
  }
}

function shouldIgnoreScreenshotElement(element: Element): boolean {
  return Boolean(element.closest('[data-floating-feedback], [data-feedback-modal]'))
}

interface ViewportCaptureOptions {
  backgroundColor: string
  viewportWidth: number
  viewportHeight: number
  scrollX: number
  scrollY: number
}

async function captureViewportWithHtml2Canvas({
  backgroundColor,
  viewportWidth,
  viewportHeight,
  scrollX,
  scrollY,
}: ViewportCaptureOptions): Promise<HTMLCanvasElement> {
  const { default: html2canvas } = await import('html2canvas')

  return html2canvas(document.body, {
    allowTaint: false,
    backgroundColor,
    foreignObjectRendering: false,
    height: viewportHeight,
    ignoreElements: shouldIgnoreScreenshotElement,
    imageTimeout: 5000,
    logging: false,
    onclone: (clonedDocument) => {
      clonedDocument.documentElement.style.position = 'relative'
      clonedDocument.body.style.position = 'relative'
    },
    scale: 1,
    scrollX,
    scrollY,
    useCORS: true,
    width: viewportWidth,
    windowHeight: viewportHeight,
    windowWidth: viewportWidth,
    x: scrollX,
    y: scrollY,
  })
}

async function captureViewportWithHtmlToImage({
  backgroundColor,
  viewportWidth,
  viewportHeight,
  scrollX,
  scrollY,
}: ViewportCaptureOptions): Promise<HTMLCanvasElement> {
  const { toCanvas } = await import('html-to-image')
  const documentWidth = Math.max(viewportWidth, document.documentElement.scrollWidth)
  const documentHeight = Math.max(viewportHeight, document.documentElement.scrollHeight)

  return toCanvas(document.documentElement, {
    backgroundColor,
    cacheBust: true,
    canvasHeight: viewportHeight,
    canvasWidth: viewportWidth,
    filter: (node) => !(node instanceof Element) || !shouldIgnoreScreenshotElement(node),
    height: viewportHeight,
    imagePlaceholder: TRANSPARENT_IMAGE_PLACEHOLDER,
    pixelRatio: 1,
    skipFonts: false,
    style: {
      height: `${documentHeight}px`,
      transform: `translate(${-scrollX}px, ${-scrollY}px)`,
      transformOrigin: 'top left',
      width: `${documentWidth}px`,
    },
    width: viewportWidth,
  })
}

async function captureEncodedViewport(
  options: ViewportCaptureOptions
): Promise<{ blob: Blob; dataUrl: string }> {
  try {
    const canvas = await captureViewportWithHtml2Canvas(options)
    return await encodeScreenshotCanvas(canvas)
  } catch (primaryError) {
    if (isTooLargeError(primaryError)) {
      throw primaryError
    }

    console.warn(
      'Primary screenshot capture failed, retrying with fallback renderer:',
      primaryError
    )
  }

  const fallbackCanvas = await captureViewportWithHtmlToImage(options)
  return encodeScreenshotCanvas(fallbackCanvas)
}

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { resolvedTheme } = useTheme()

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('bug')

  // Bug form state
  const [bugTitle, setBugTitle] = useState('')
  const [bugMessage, setBugMessage] = useState('')
  const [screenshotState, setScreenshotState] = useState<ScreenshotState>('idle')
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null)
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null)
  const [bugSubmitting, setBugSubmitting] = useState(false)
  const [bugSuccess, setBugSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Idea form state (quick submit)
  const [ideaTitle, setIdeaTitle] = useState('')
  const [ideaDescription, setIdeaDescription] = useState('')
  const [ideaArea, setIdeaArea] = useState<FeatureArea | ''>('')
  const [ideaSubmitting, setIdeaSubmitting] = useState(false)
  const [ideaSuccess, setIdeaSuccess] = useState(false)
  const [createdIdeaId, setCreatedIdeaId] = useState<string | null>(null)

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBugTitle('')
      setBugMessage('')
      setScreenshotState('idle')
      setScreenshotUrl(null)
      setScreenshotPreview(null)
      setBugSubmitting(false)
      setBugSuccess(false)
      setIdeaTitle('')
      setIdeaDescription('')
      setIdeaArea('')
      setIdeaSubmitting(false)
      setIdeaSuccess(false)
      setCreatedIdeaId(null)
      setActiveTab('bug')
    }
  }, [isOpen])

  // Body scroll lock when modal is open
  // Prevents background scrolling for better UX
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Build context object
  const buildContext = useCallback((): FeedbackContext => {
    return {
      route: location.pathname + location.search,
      theme: resolvedTheme,
      userAgent: navigator.userAgent,
      viewport: {
        w: window.innerWidth,
        h: window.innerHeight,
      },
      timestamp: new Date().toISOString(),
    }
  }, [location, resolvedTheme])

  // Capture screenshot with hidden modal
  const captureScreenshot = useCallback(async () => {
    setScreenshotState('capturing')

    // Elements to hide during capture
    const elementsToHide: { el: HTMLElement; originalDisplay: string }[] = []
    let didCaptureScreenshot = false

    try {
      // Find and hide feedback UI elements directly (more reliable than CSS)
      const floatingButton = document.querySelector('[data-floating-feedback]') as HTMLElement
      const feedbackModal = document.querySelector('[data-feedback-modal]') as HTMLElement
      const modalBackdrop = feedbackModal?.parentElement?.previousElementSibling as HTMLElement

      if (floatingButton) {
        elementsToHide.push({ el: floatingButton, originalDisplay: floatingButton.style.display })
        floatingButton.style.display = 'none'
      }
      if (feedbackModal) {
        elementsToHide.push({ el: feedbackModal, originalDisplay: feedbackModal.style.display })
        feedbackModal.style.display = 'none'
      }
      if (modalBackdrop && modalBackdrop.classList.contains('fixed')) {
        elementsToHide.push({ el: modalBackdrop, originalDisplay: modalBackdrop.style.display })
        modalBackdrop.style.display = 'none'
      }

      // Also hide the modal container (parent of feedbackModal)
      const modalContainer = feedbackModal?.parentElement as HTMLElement
      if (modalContainer && modalContainer.classList.contains('fixed')) {
        elementsToHide.push({ el: modalContainer, originalDisplay: modalContainer.style.display })
        modalContainer.style.display = 'none'
      }

      // Wait for next frames to ensure UI is hidden
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      )
      await document.fonts?.ready?.catch(() => undefined)

      // Determine background color based on theme
      const isDarkMode = document.documentElement.classList.contains('dark')
      const backgroundColor = isDarkMode ? '#0f172a' : '#ffffff' // slate-900 for dark, white for light

      const viewportWidth = Math.max(1, window.innerWidth)
      const viewportHeight = Math.max(1, window.innerHeight)
      const scrollX = window.scrollX || document.documentElement.scrollLeft || 0
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0

      // Capture and encode the visible viewport, not the full scrollable page.
      const { blob, dataUrl } = await captureEncodedViewport({
        backgroundColor,
        viewportWidth,
        viewportHeight,
        scrollX,
        scrollY,
      })
      restoreHiddenElements(elementsToHide)

      if (blob.size > MAX_SCREENSHOT_BYTES) {
        setScreenshotState('too-large')
        return
      }

      setScreenshotPreview(dataUrl)
      didCaptureScreenshot = true

      setScreenshotState('uploading')

      const file = new File([blob], 'feedback-screenshot.jpg', { type: blob.type })
      const uploadResult = await api.feedback.uploadScreenshot(file)
      setScreenshotUrl(uploadResult.url)
      setScreenshotState('ready')
    } catch (error) {
      console.error('Screenshot capture failed:', error)
      restoreHiddenElements(elementsToHide)
      setScreenshotUrl(null)
      if (didCaptureScreenshot) {
        setScreenshotState('upload-failed')
      } else {
        setScreenshotState(isTooLargeError(error) ? 'too-large' : 'failed')
      }
    }
  }, [])

  // Handle file upload
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a PNG, JPEG, or WebP image')
      return
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setScreenshotState('too-large')
      return
    }

    setScreenshotState('uploading')

    try {
      // Create preview
      const reader = new FileReader()
      reader.onload = (event) => {
        setScreenshotPreview(event.target?.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to server
      const uploadResult = await api.feedback.uploadScreenshot(file)
      setScreenshotUrl(uploadResult.url)
      setScreenshotState('ready')
    } catch (error) {
      console.error('File upload failed:', error)
      setScreenshotState('failed')
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // Remove screenshot
  const removeScreenshot = useCallback(() => {
    setScreenshotState('idle')
    setScreenshotUrl(null)
    setScreenshotPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // Submit bug report
  const handleBugSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (bugMessage.length < 10) {
      toast.error('Please describe the bug in at least 10 characters')
      return
    }

    setBugSubmitting(true)

    try {
      await api.feedback.submitBug({
        title: bugTitle || undefined,
        message: bugMessage,
        screenshot_url: screenshotUrl || undefined,
        context: buildContext(),
      })

      setBugSuccess(true)
      toast.success('Bug report submitted. Thanks for helping improve Optioneer!')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit bug report'
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        toast.error("You've submitted several reports recently. Please wait before sending more.")
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setBugSubmitting(false)
    }
  }

  // Submit quick idea
  const handleIdeaSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (ideaTitle.length < 3) {
      toast.error('Please add a title (at least 3 characters)')
      return
    }
    if (ideaDescription.length < 10) {
      toast.error('Please describe your request in at least 10 characters')
      return
    }

    setIdeaSubmitting(true)

    try {
      const result = await api.feedback.submitFeature({
        title: ideaTitle,
        description: ideaDescription,
        area: ideaArea || undefined,
        context: buildContext(),
      })

      setCreatedIdeaId(result.id)
      setIdeaSuccess(true)
      toast.success('Request submitted! Check it out on the Feature Requests board.')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit request'
      if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
        toast.error("You've submitted several requests recently. Please wait before sending more.")
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setIdeaSubmitting(false)
    }
  }

  // Navigate to Feature Requests board
  const goToIdeasBoard = () => {
    onClose()
    navigate('/feedback')
  }

  // View created idea
  const viewCreatedIdea = () => {
    onClose()
    navigate(`/feedback?feature=${createdIdeaId}`)
  }

  /**
   * PORTAL FIX: This modal is rendered via Portal to document.body to escape
   * any stacking context issues from parent containers (Navigation component).
   *
   * Original bug: Modal was clipped at the top when opened from the Navigation
   * header because it was inside the Navigation's DOM tree which creates a new
   * stacking context via: fixed positioning, backdrop-blur, or transform animations.
   *
   * Using z-[100] to ensure modal stays above all app content (header is z-50).
   */
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - z-[100] to stay above header's z-50 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 dark:bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container - Grid for perfect centering, z-[100] for stacking */}
          <div
            className="fixed inset-0 z-[100] grid place-items-center p-4 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-modal-title"
          >
            {/* Modal */}
            <motion.div
              data-feedback-modal
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'relative w-full rounded-2xl',
                // Viewport-safe sizing: max-w and max-h with scroll
                'max-w-lg max-h-[calc(100vh-2rem)]',
                'overflow-y-auto',
                'bg-white dark:bg-slate-900',
                'shadow-2xl dark:shadow-black/50',
                'border border-slate-200 dark:border-slate-800',
                'my-4'
              )}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2
                      id="feedback-modal-title"
                      className="text-lg font-semibold text-slate-900 dark:text-white"
                    >
                      Send Feedback
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Tell me what's missing — I'm building Optioneer in public.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className={cn(
                      'p-1.5 rounded-lg transition-colors',
                      'text-slate-400 hover:text-slate-600',
                      'dark:text-slate-500 dark:hover:text-slate-300',
                      'hover:bg-slate-100 dark:hover:bg-slate-800'
                    )}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Segmented Tabs */}
                <div className="mt-4 flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab('bug')}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md',
                      'text-sm font-medium transition-all duration-150',
                      activeTab === 'bug'
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    )}
                  >
                    <Bug className="h-4 w-4" />
                    Bug
                  </button>
                  <button
                    onClick={() => setActiveTab('idea')}
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md',
                      'text-sm font-medium transition-all duration-150',
                      activeTab === 'idea'
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    )}
                  >
                    <Lightbulb className="h-4 w-4" />
                    Feature request
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="px-6 pb-6">
                {/* Bug Tab */}
                {activeTab === 'bug' && (
                  <>
                    {bugSuccess ? (
                      <div className="py-8 text-center">
                        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                          Thanks for the report!
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                          This helps improve Optioneer for everyone.
                        </p>
                        <button
                          onClick={onClose}
                          className={cn(
                            'px-4 py-2 rounded-lg font-medium',
                            'bg-brand-600 dark:bg-amber-500 text-white dark:text-slate-900',
                            'hover:bg-brand-500 dark:hover:bg-amber-400',
                            'transition-colors'
                          )}
                        >
                          Close
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleBugSubmit} className="space-y-4">
                        {/* Title (optional) */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                            Title <span className="text-slate-400 font-normal">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={bugTitle}
                            onChange={(e) => setBugTitle(e.target.value)}
                            placeholder="Brief summary of the issue"
                            maxLength={200}
                            className={cn(
                              'w-full px-3 py-2 rounded-lg border',
                              'bg-white dark:bg-slate-800',
                              'border-slate-200 dark:border-slate-700',
                              'text-slate-900 dark:text-white',
                              'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                              'focus:outline-none focus:ring-2',
                              'focus:ring-brand-500 dark:focus:ring-amber-500',
                              'focus:border-transparent'
                            )}
                          />
                        </div>

                        {/* Message (required) */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                            What happened? <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={bugMessage}
                            onChange={(e) => setBugMessage(e.target.value)}
                            placeholder="Describe what you were doing and what went wrong..."
                            required
                            minLength={10}
                            maxLength={2000}
                            rows={4}
                            className={cn(
                              'w-full px-3 py-2 rounded-lg border resize-none',
                              'bg-white dark:bg-slate-800',
                              'border-slate-200 dark:border-slate-700',
                              'text-slate-900 dark:text-white',
                              'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                              'focus:outline-none focus:ring-2',
                              'focus:ring-brand-500 dark:focus:ring-amber-500',
                              'focus:border-transparent'
                            )}
                          />
                          <p className="mt-1 text-xs text-slate-400">
                            {bugMessage.length}/2000 characters
                          </p>
                        </div>

                        {/* Screenshot section */}
                        <div className="space-y-3">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            Attach screenshot{' '}
                            <span className="text-slate-400 font-normal">(optional)</span>
                          </label>

                          {/* Screenshot preview */}
                          {screenshotPreview && (
                            <div className="relative group">
                              <img
                                src={screenshotPreview}
                                alt="Screenshot preview"
                                className={cn(
                                  'w-full max-h-32 object-cover rounded-lg border',
                                  'border-slate-200 dark:border-slate-700'
                                )}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={captureScreenshot}
                                  disabled={
                                    screenshotState === 'capturing' ||
                                    screenshotState === 'uploading'
                                  }
                                  className="p-2 bg-white/90 rounded-lg text-slate-700 hover:bg-white transition-colors"
                                  title="Retake screenshot"
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={removeScreenshot}
                                  className="p-2 bg-white/90 rounded-lg text-red-600 hover:bg-white transition-colors"
                                  title="Remove screenshot"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              {screenshotState === 'ready' && (
                                <span className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                                  Ready
                                </span>
                              )}
                              {screenshotState === 'uploading' && (
                                <span className="absolute top-2 right-2 px-2 py-0.5 bg-slate-500 text-white text-xs rounded-full flex items-center gap-1">
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                  Uploading
                                </span>
                              )}
                            </div>
                          )}

                          {/* Capture/Upload buttons (show when no preview) */}
                          {!screenshotPreview && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={captureScreenshot}
                                disabled={
                                  screenshotState === 'capturing' || screenshotState === 'uploading'
                                }
                                className={cn(
                                  'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium',
                                  'border border-slate-200 dark:border-slate-700',
                                  'bg-white dark:bg-slate-800',
                                  'text-slate-700 dark:text-slate-300',
                                  'hover:bg-slate-50 dark:hover:bg-slate-700',
                                  'disabled:opacity-50 disabled:cursor-not-allowed',
                                  'transition-colors'
                                )}
                              >
                                {screenshotState === 'capturing' ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Capturing...
                                  </>
                                ) : (
                                  <>
                                    <Camera className="h-4 w-4" />
                                    Capture screen
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={
                                  screenshotState === 'capturing' || screenshotState === 'uploading'
                                }
                                className={cn(
                                  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm',
                                  'border border-slate-200 dark:border-slate-700',
                                  'bg-white dark:bg-slate-800',
                                  'text-slate-500 dark:text-slate-400',
                                  'hover:bg-slate-50 dark:hover:bg-slate-700',
                                  'disabled:opacity-50 disabled:cursor-not-allowed',
                                  'transition-colors'
                                )}
                                title="Upload image"
                              >
                                <Upload className="h-4 w-4" />
                              </button>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleFileUpload}
                                className="hidden"
                              />
                            </div>
                          )}

                          {/* Status messages */}
                          {screenshotState === 'too-large' && (
                            <p className="text-xs text-amber-500">
                              Image too large (max 2MB). Try a smaller image.
                            </p>
                          )}
                          {screenshotState === 'failed' && (
                            <p className="text-xs text-red-500">
                              Capture failed. Try again or upload an image.
                            </p>
                          )}
                          {screenshotState === 'upload-failed' && (
                            <p className="text-xs text-red-500">
                              Screenshot captured, but upload failed. Try again or upload an image.
                            </p>
                          )}
                        </div>

                        {/* Submit */}
                        <button
                          type="submit"
                          disabled={bugSubmitting || bugMessage.length < 10}
                          className={cn(
                            'w-full py-2.5 rounded-lg font-medium',
                            'bg-brand-600 dark:bg-amber-500 text-white dark:text-slate-900',
                            'hover:bg-brand-500 dark:hover:bg-amber-400',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            'transition-colors flex items-center justify-center gap-2'
                          )}
                        >
                          {bugSubmitting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            'Send bug report'
                          )}
                        </button>
                      </form>
                    )}
                  </>
                )}

                {/* Feature Request Tab */}
                {activeTab === 'idea' && (
                  <>
                    {ideaSuccess ? (
                      <div className="py-8 text-center">
                        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                          Request submitted!
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                          Thanks for sharing. Check it out on the Feature Requests board.
                        </p>
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={viewCreatedIdea}
                            className={cn(
                              'px-4 py-2 rounded-lg font-medium',
                              'bg-brand-600 dark:bg-amber-500 text-white dark:text-slate-900',
                              'hover:bg-brand-500 dark:hover:bg-amber-400',
                              'transition-colors flex items-center gap-2'
                            )}
                          >
                            View request
                            <ExternalLink className="h-4 w-4" />
                          </button>
                          <button
                            onClick={onClose}
                            className={cn(
                              'px-4 py-2 rounded-lg font-medium',
                              'bg-slate-100 dark:bg-slate-800',
                              'text-slate-700 dark:text-slate-300',
                              'hover:bg-slate-200 dark:hover:bg-slate-700',
                              'transition-colors'
                            )}
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {/* Open Feature Requests CTA */}
                        <button
                          onClick={goToIdeasBoard}
                          className={cn(
                            'w-full py-3 rounded-lg font-medium',
                            'bg-gradient-to-r from-brand-600 to-brand-500',
                            'dark:from-amber-500 dark:to-amber-400',
                            'text-white dark:text-slate-900',
                            'hover:from-brand-500 hover:to-brand-400',
                            'dark:hover:from-amber-400 dark:hover:to-amber-300',
                            'transition-all flex items-center justify-center gap-2',
                            'shadow-md hover:shadow-lg'
                          )}
                        >
                          Open Feature Requests
                          <ExternalLink className="h-4 w-4" />
                        </button>

                        {/* Divider */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            or send a quick request
                          </span>
                          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                        </div>

                        {/* Quick Feature Request Form */}
                        <form onSubmit={handleIdeaSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                              Title <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={ideaTitle}
                              onChange={(e) => setIdeaTitle(e.target.value)}
                              placeholder="What would you like to see?"
                              required
                              minLength={3}
                              maxLength={200}
                              className={cn(
                                'w-full px-3 py-2 rounded-lg border',
                                'bg-white dark:bg-slate-800',
                                'border-slate-200 dark:border-slate-700',
                                'text-slate-900 dark:text-white',
                                'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                                'focus:outline-none focus:ring-2',
                                'focus:ring-brand-500 dark:focus:ring-amber-500',
                                'focus:border-transparent'
                              )}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                              Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              value={ideaDescription}
                              onChange={(e) => setIdeaDescription(e.target.value)}
                              placeholder="Describe how it would work..."
                              required
                              minLength={10}
                              maxLength={2000}
                              rows={3}
                              className={cn(
                                'w-full px-3 py-2 rounded-lg border resize-none',
                                'bg-white dark:bg-slate-800',
                                'border-slate-200 dark:border-slate-700',
                                'text-slate-900 dark:text-white',
                                'placeholder:text-slate-400 dark:placeholder:text-slate-500',
                                'focus:outline-none focus:ring-2',
                                'focus:ring-brand-500 dark:focus:ring-amber-500',
                                'focus:border-transparent'
                              )}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                              Area <span className="text-slate-400 font-normal">(optional)</span>
                            </label>
                            <select
                              value={ideaArea}
                              onChange={(e) => setIdeaArea(e.target.value as FeatureArea | '')}
                              className={cn(
                                'w-full px-3 py-2 rounded-lg border',
                                'bg-white dark:bg-slate-800',
                                'border-slate-200 dark:border-slate-700',
                                'text-slate-900 dark:text-white',
                                'focus:outline-none focus:ring-2',
                                'focus:ring-brand-500 dark:focus:ring-amber-500',
                                'focus:border-transparent'
                              )}
                            >
                              <option value="">Select an area...</option>
                              {AREAS.map((area) => (
                                <option key={area} value={area}>
                                  {area}
                                </option>
                              ))}
                            </select>
                          </div>

                          <button
                            type="submit"
                            disabled={
                              ideaSubmitting || ideaTitle.length < 3 || ideaDescription.length < 10
                            }
                            className={cn(
                              'w-full py-2.5 rounded-lg font-medium',
                              'bg-slate-100 dark:bg-slate-800',
                              'text-slate-700 dark:text-slate-300',
                              'hover:bg-slate-200 dark:hover:bg-slate-700',
                              'disabled:opacity-50 disabled:cursor-not-allowed',
                              'transition-colors flex items-center justify-center gap-2',
                              'border border-slate-200 dark:border-slate-700'
                            )}
                          >
                            {ideaSubmitting ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              'Send Quick Request'
                            )}
                          </button>
                        </form>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer note */}
              <div className="px-6 pb-4">
                <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
                  No sensitive details please (positions, account info).
                </p>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
